(() => {
  if (window.IslanderDelivery) return;
  const contextNode = document.querySelector('[data-islander-delivery-context]');
  if (!contextNode) return;
  let config;
  try { config = JSON.parse(contextNode.textContent); } catch { return; }

  const FEATURE = 'free_uk_delivery_oct26';
  const EXPERIMENT = 'free-uk-delivery-amount-bar';
  const REVISION = 'delivery-20261023-v1';
  const ASSIGNMENT = 'islander_delivery_assignment';
  const OPTOUT = 'islander_delivery_optout';
  const CART_ATTRIBUTE = '__islander_delivery_v1';
  const LINE_PROPERTY = '_islander_delivery_v1';
  const SDK_URL = 'https://cdn.jsdelivr.net/npm/@growthbook/growthbook@1.8.0/dist/bundles/index.min.js';
  const TTL = 30 * 86400000;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const validPhase = value => /^(?:0|[1-9]\d{0,5})$/.test(String(value));
  const url = new URL(window.location.href);
  const forced = url.searchParams.get('ab_force')?.match(/^free_delivery_uk:([AB])$/);
  const qa = config.themeRole !== 'main' || config.editor === true || url.protocol !== 'https:' ||
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(url.hostname) ||
    [...url.searchParams.keys()].some(key => /^(ab_force|workshop_variant|workshop_debug|preview_theme_id|new[-_]workshop[-_]page(?:_oct26)?$|gb[-_~]|growthbook[-_]|force[-_])/i.test(key));
  const eligibleCountry = () => config.country === 'GB' && config.currency === 'GBP' && cart?.currency === 'GBP';
  const allowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true &&
        window.Shopify.customerPrivacy.currentVisitorConsent?.().analytics !== 'no';
    } catch { return false; }
  };
  const policy = (arm = 'A', phase = 'inactive') => ({v: 1, experiment: EXPERIMENT, phase: String(phase), arm, revision: REVISION});
  const cookies = name => {
    try {
      return document.cookie.split(';').flatMap(value => {
        const split = value.indexOf('=');
        return split >= 0 && value.slice(0, split).trim() === name ? [value.slice(split + 1).trim()] : [];
      });
    } catch { return []; }
  };
  let emergencyOptout = false, channel;
  const optedOut = () => emergencyOptout || cookies(OPTOUT).includes('1');
  const writeCookie = (name, value, seconds) => {
    if (qa) return false;
    try {
      const previous = cookies(name).join(';');
      document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${seconds}; SameSite=Lax; Secure`;
      const saved = seconds === 0 ? cookies(name).length === 0 : cookies(name).includes(encodeURIComponent(value));
      if (saved && previous !== cookies(name).join(';')) channel?.postMessage({revision: REVISION});
      return saved;
    } catch { return false; }
  };
  const readAssignment = () => {
    for (const raw of cookies(ASSIGNMENT)) {
      try {
        const value = JSON.parse(decodeURIComponent(raw));
        if (value.v === 1 && value.experiment === EXPERIMENT && value.revision === REVISION && UUID.test(value.id) &&
          validPhase(value.phase) && ['A', 'B'].includes(value.arm) && Number.isSafeInteger(value.expiresAt) &&
          value.expiresAt > Date.now() && value.expiresAt <= Date.now() + TTL + 300000) return value;
      } catch {}
    }
  };
  const money = cents => new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2,
  }).format(cents / 100);
  const sameObject = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const setAttribute = (element, name, value) => {
    if (element?.getAttribute(name) !== String(value)) element?.setAttribute(name, String(value));
  };
  const setText = (element, value) => { if (element && element.textContent !== value) element.textContent = value; };

  let cart = config.cart || {currency: config.currency, items_subtotal_price: 0, item_count: 0, requires_shipping: false};
  let desired = JSON.stringify(policy()), synced = '', acknowledgedPolicy = '', policyResolved = false, running = null, attempts = 0, lastError = '';
  let features, selected, assignment, sdkPromise, featureRequest, lastFeatureCheck = 0;
  let refreshRequest, refreshQueued = false, cartGeneration = 0, cartFresh = true, lastCopy = '', lastOptout = optedOut();
  const waiters = new Set(), exposed = new Set(), pendingCheckout = new WeakSet();
  const checkoutReady = new WeakMap();
  const headerRoots = () => [...document.querySelectorAll('islander-announcement[data-delivery-display]')];
  const displayMode = () => headerRoots()[0]?.dataset.deliveryDisplay || 'progress';
  const active = () => eligibleCountry() && displayMode() !== 'off';
  // A market selector is not a withdrawal of an already acknowledged UK offer.
  const initialAssignment = !qa && displayMode() !== 'off' && !optedOut() ? readAssignment() : undefined;
  if (initialAssignment?.acknowledged === true) {
    assignment = initialAssignment;
    desired = acknowledgedPolicy = JSON.stringify(policy(assignment.arm, assignment.phase));
    policyResolved = true;
  } else if (qa || !active() || optedOut()) policyResolved = true;
  const currentPolicy = () => {
    const requested = JSON.parse(desired);
    // A positive offer is shown only after its cart policy has reached Shopify.
    return requested.arm === 'B' && desired !== acknowledgedPolicy && !qa ? policy('A', requested.phase) : requested;
  };
  const subtotal = () => Number.isSafeInteger(cart.items_subtotal_price) && cart.items_subtotal_price >= 0 ? cart.items_subtotal_price : 0;
  const needsShipping = () => cart.requires_shipping === true || (cart.items || []).some(item => item.requires_shipping === true);
  const view = () => {
    const current = currentPolicy(), threshold = current.arm === 'B' ? 7500 : 8500;
    const amount = needsShipping() && cartFresh ? subtotal() : 0;
    const complete = needsShipping() && cartFresh && amount >= threshold;
    const staticCopy = `Free UK delivery from ${money(threshold)}`;
    const copy = complete ? 'Free UK delivery for you!' : amount > 0 ? `Only ${money(threshold - amount)} to free UK delivery` : staticCopy;
    return {current, threshold, amount, complete, staticCopy, copy, progress: Math.min(1, Math.max(0, amount / threshold))};
  };
  const renderMeter = (root, state) => {
    root.classList.toggle('is-complete', state.complete);
    const progress = String(state.progress);
    if (root.style.getPropertyValue('--delivery-progress') !== progress) root.style.setProperty('--delivery-progress', progress);
    const copy = root.hasAttribute('data-delivery-header') && !state.complete ? state.staticCopy : state.copy;
    setText(root.querySelector('[data-delivery-copy]'), copy);
    const meter = root.querySelector('[data-delivery-meter]');
    setAttribute(meter, 'aria-valuemax', state.threshold);
    setAttribute(meter, 'aria-valuenow', Math.min(state.amount, state.threshold));
    setAttribute(meter, 'aria-valuetext', state.complete ? state.copy : `${money(state.amount)} towards ${money(state.threshold)} for free UK delivery`);
  };
  const renderGuidance = root => {
    root.hidden = !active() || !needsShipping();
    if (!root.hidden) renderMeter(root, view());
  };
  const productionExposure = () => {
    if (qa || !active() || optedOut() || !selected || !assignment || !allowed() || desired !== synced) return;
    const state = view();
    if (state.current.arm !== assignment.arm) return;
    const rendered = headerRoots().some(root => {
      const message = root.querySelector(root.dataset.deliveryActive === 'true' ? '[data-delivery-header]' : '[data-delivery-message]');
      return !root.hidden && message && !message.hidden && message.getAttribute('aria-hidden') !== 'true';
    });
    if (!rendered) return;
    const attribution = window.IslanderExperimentAttribution;
    if (features) attribution?.configure(features); // The registry needs the FULL feature payload.
    const phase = attribution?.phaseFor?.(features, FEATURE, EXPERIMENT);
    if (phase === assignment.phase) attribution?.record(selected, {phase, renderedValue: state.current.arm === 'B'});
    const signature = `${assignment.phase}:${assignment.arm}`;
    if (exposed.has(signature)) return;
    try {
      const published = window.Shopify?.analytics?.publish?.('islander:interaction', {
        event_name: 'experiment_viewed', experiment_id: EXPERIMENT, variation_id: assignment.arm === 'B' ? '1' : '0',
      });
      if (published === undefined && typeof window.Shopify?.analytics?.publish !== 'function') return;
      exposed.add(signature);
      published?.catch?.(() => exposed.delete(signature));
    } catch {}
  };
  const render = () => {
    const state = view(), showProgress = active() && displayMode() === 'progress';
    for (const root of headerRoots()) {
      const before = root.dataset.deliveryActive;
      root.dataset.deliveryActive = String(showProgress);
      let presentationChanged = before !== root.dataset.deliveryActive;
      const track = root.querySelector('.islander-announcement__track');
      if (track) track.hidden = false;
      root.querySelectorAll('[data-delivery-message]').forEach(message => {
        const wasHidden = message.hidden;
        message.hidden = !eligibleCountry() || showProgress;
        setText(message.querySelector('[data-delivery-static-copy]'), state.staticCopy);
        if (wasHidden !== message.hidden) presentationChanged = true;
      });
      const progress = root.querySelector('[data-delivery-header]');
      const background = root.querySelector('[data-delivery-background]');
      if (background) {
        background.hidden = !showProgress;
        if (showProgress) renderMeter(background, state);
      }
      if (progress) {
        if (progress.hidden !== !showProgress) presentationChanged = true;
        progress.hidden = !showProgress;
        if (showProgress) renderMeter(progress, state);
      }
      const staticMessage = root.querySelector('[data-delivery-message]:not([hidden])');
      root.dataset.deliveryRendered = String(active() && (showProgress || (staticMessage && staticMessage.getAttribute('aria-hidden') !== 'true')));
      if (presentationChanged) root.dispatchEvent(new window.CustomEvent('islander:delivery-display'));
    }
    document.querySelectorAll('islander-delivery-guidance').forEach(renderGuidance);
    const status = document.querySelector('[data-delivery-announcement]');
    if (lastCopy && lastCopy !== state.copy && active()) setText(status, state.copy);
    lastCopy = state.copy;
    document.querySelectorAll('[data-delivery-test-enabled]').forEach(toggle => { toggle.checked = !optedOut(); });
    document.querySelectorAll('[data-delivery-test-status]').forEach(status => setText(status, optedOut() ? 'Delivery test off. Standard UK delivery applies.' : ''));
    productionExposure();
  };
  const localFormURL = form => {
    try { const value = new URL(form.action, url.href); return value.origin === url.origin ? value : null; } catch { return null; }
  };
  const syncForms = () => {
    if (qa) return;
    const value = JSON.stringify(currentPolicy());
    for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
      if (!/\/cart\/add(?:\.js)?\/?$/.test(localFormURL(form)?.pathname || '')) continue;
      const name = `properties[${LINE_PROPERTY}]`;
      const inputs = [...form.querySelectorAll(`input[name="${name}"]`)];
      const input = inputs.shift() || document.createElement('input');
      for (const duplicate of inputs) duplicate.remove();
      // Shopify observes these fields; unchanged writes can re-render payment buttons forever.
      if (input.type !== 'hidden') input.type = 'hidden';
      if (input.name !== name) input.name = name;
      if (input.value !== value) input.value = value;
      if (input.disabled) input.disabled = false;
      if (!input.parentNode) form.append(input);
    }
  };
  const notify = () => {
    if (desired !== synced && (running || attempts < 2)) return;
    for (const waiter of waiters) { window.clearTimeout(waiter.timer); waiter.resolve(desired === synced); }
    waiters.clear();
  };
  const endpoint = path => {
    const root = new URL(window.Shopify?.routes?.root || '/', url.origin);
    if (root.origin !== url.origin || !root.pathname.endsWith('/')) throw Error('Invalid cart route');
    return new URL(path, root).href;
  };
  const pump = () => {
    // An unresolved new tab must not overwrite another tab's acknowledged offer with A.
    if (qa || !policyResolved || running || desired === synced || attempts >= 2) { notify(); return; }
    const body = desired, controller = new window.AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 750);
    attempts++;
    running = Promise.resolve().then(() => window.fetch(endpoint('cart/update.js'), {
      method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({attributes: {[CART_ATTRIBUTE]: body}}), signal: controller.signal,
    })).then(response => {
      if (!response.ok) throw Error('Delivery policy could not be saved');
      synced = acknowledgedPolicy = body; lastError = '';
      const saved = readAssignment(), applied = JSON.parse(body);
      if (!optedOut() && saved && assignment && saved.id === assignment.id && saved.phase === applied.phase && saved.arm === applied.arm && saved.acknowledged !== true) {
        assignment = {...saved, acknowledged: true};
        writeCookie(ASSIGNMENT, JSON.stringify(assignment), Math.max(1, Math.floor((assignment.expiresAt - Date.now()) / 1000)));
      }
    }).catch(() => { lastError = 'cart_sync_failed'; }).finally(() => {
      window.clearTimeout(timeout); running = null;
      syncForms(); render();
      if (body !== desired || (desired !== synced && attempts < 2)) pump(); else notify();
    });
  };
  const applyPolicy = next => {
    policyResolved = true;
    const nextValue = JSON.stringify(next);
    if (nextValue !== desired) { desired = nextValue; attempts = 0; }
    syncForms(); render(); pump();
  };
  const reconcileShared = () => {
    if (qa) return;
    if (optedOut() || displayMode() === 'off') {
      selected = assignment = undefined; removeMeasurement(); applyPolicy(policy()); return;
    }
    const saved = readAssignment();
    if (saved?.acknowledged === true) {
      const changed = !assignment || saved.id !== assignment.id || saved.phase !== assignment.phase || saved.arm !== assignment.arm;
      if (changed) { selected = undefined; removeMeasurement(); }
      assignment = saved;
      acknowledgedPolicy = JSON.stringify(policy(saved.arm, saved.phase));
      applyPolicy(policy(saved.arm, saved.phase));
    } else if (!active() || (!saved && assignment)) {
      selected = assignment = undefined; removeMeasurement(); applyPolicy(policy());
    }
  };
  const flush = async (timeoutMs = 1000) => {
    if (qa) return false;
    const budget = Number.isFinite(timeoutMs) ? Math.min(2000, Math.max(0, timeoutMs)) : 1000;
    const started = Date.now();
    try {
      reconcileShared();
      if (!policyResolved) {
        let timer;
        await Promise.race([ready, new Promise(resolve => { timer = window.setTimeout(resolve, budget); })]);
        window.clearTimeout(timer);
        reconcileShared();
        if (!policyResolved) return false;
      }
      syncForms();
      // Shopify's cart is shared between tabs; a tab-local success cache is not checkout proof.
      if (!running) { synced = ''; attempts = 0; }
      pump();
      return await new Promise(resolve => {
        const remaining = Math.max(0, budget - (Date.now() - started));
        const waiter = {resolve, timer: window.setTimeout(() => { waiters.delete(waiter); resolve(false); }, remaining)};
        waiters.add(waiter); notify();
      });
    } catch { lastError = 'cart_sync_failed'; return false; }
  };
  const useCart = next => {
    if (!next || !Number.isSafeInteger(next.items_subtotal_price) || !Number.isSafeInteger(next.item_count) || typeof next.currency !== 'string') return false;
    cart = next;
    cartFresh = true;
    if (!eligibleCountry()) { selected = undefined; reconcileShared(); render(); }
    else render();
    return true;
  };
  const refreshCart = () => {
    if (refreshRequest) { refreshQueued = true; return refreshRequest; }
    const generation = ++cartGeneration;
    const controller = new window.AbortController();
    const timer = window.setTimeout(() => controller.abort(), 1500);
    refreshRequest = Promise.resolve().then(() => window.fetch(endpoint('cart.js'), {credentials: 'same-origin', headers: {Accept: 'application/json'}, signal: controller.signal}))
      .then(response => { if (!response.ok) throw Error('Cart unavailable'); return response.json(); })
      .then(next => { if (generation === cartGeneration) useCart(next); })
      .catch(() => { lastError = 'cart_refresh_failed'; })
      .finally(() => {
        window.clearTimeout(timer);
        refreshRequest = null;
        if (refreshQueued) { refreshQueued = false; refreshCart(); }
      });
    return refreshRequest;
  };
  const loadSDK = () => {
    if (window.growthbook?.GrowthBook) return Promise.resolve(window.growthbook.GrowthBook);
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${SDK_URL}"]`);
      const timer = window.setTimeout(() => reject(Error('Delivery SDK timed out')), 2000);
      const complete = () => { window.clearTimeout(timer); window.growthbook?.GrowthBook ? resolve(window.growthbook.GrowthBook) : reject(Error('Delivery SDK unavailable')); };
      if (!script) {
        script = document.createElement('script'); script.src = SDK_URL; script.async = true;
        script.dataset.islanderDeliverySdk = '';
        script.addEventListener('load', complete, {once: true});
        script.addEventListener('error', () => { window.clearTimeout(timer); reject(Error('Delivery SDK unavailable')); }, {once: true});
        document.head.append(script);
      } else {
        script.addEventListener('load', complete, {once: true});
        script.addEventListener('error', () => { window.clearTimeout(timer); reject(Error('Delivery SDK unavailable')); }, {once: true});
      }
    }).catch(error => { sdkPromise = undefined; throw error; });
    return sdkPromise;
  };
  const validatedRule = payload => {
    const rules = payload?.[FEATURE]?.rules;
    if (!Array.isArray(rules)) return;
    const candidates = rules.filter(rule => rule.key === EXPERIMENT && validPhase(rule.phase) &&
      !Object.prototype.hasOwnProperty.call(rule, 'force') && rule.active !== false && !['draft', 'stopped'].includes(rule.status) &&
      rule.coverage !== 0 && sameObject(rule.variations, [false, true]));
    return candidates.length === 1 ? candidates[0] : undefined;
  };
  const removeMeasurement = () => { if (!qa) window.IslanderExperimentAttribution?.remove(EXPERIMENT); };
  const refresh = () => {
    if (qa) { applyPolicy(policy(forced?.[1] || 'A', 'qa')); return Promise.resolve(); }
    if (!active() || optedOut()) {
      selected = undefined; removeMeasurement();
      if (optedOut() || displayMode() === 'off') writeCookie(ASSIGNMENT, '', 0);
      reconcileShared();
      return Promise.resolve();
    }
    if (featureRequest) return featureRequest;
    lastFeatureCheck = Date.now();
    const controller = new window.AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2500);
    featureRequest = Promise.all([
      Promise.resolve().then(() => window.fetch(`https://cdn.growthbook.io/api/features/${config.clientKey}`, {credentials: 'omit', signal: controller.signal})).then(response => {
        if (!response.ok) throw Error('Delivery feature unavailable'); return response.json();
      }), loadSDK(),
    ]).then(([payload, GrowthBook]) => {
      if (!active() || optedOut()) { selected = undefined; removeMeasurement(); reconcileShared(); return; }
      if (!payload?.features || typeof payload.features !== 'object' || Array.isArray(payload.features)) throw Error('Delivery feature unavailable');
      features = payload.features;
      const rule = validatedRule(features);
      if (!rule) throw Object.assign(Error('Delivery experiment inactive'), {retired: true});
      const stored = readAssignment();
      const id = stored?.id || window.crypto.randomUUID();
      const gb = new GrowthBook({features, attributes: {id, country: config.country, currency: config.currency, themeId: config.themeId}, trackingCallback: () => {}});
      let feature;
      try { feature = gb.evalFeature(FEATURE); } finally { gb.destroy(); }
      const result = feature?.experimentResult, variation = result?.variationId;
      if (feature?.source !== 'experiment' || feature.experiment?.key !== EXPERIMENT || feature.experiment.active === false ||
        ['draft', 'stopped'].includes(feature.experiment.status) || result?.inExperiment !== true || result.hashUsed !== true ||
        ![0, 1].includes(variation) || feature.value !== [false, true][variation] ||
        !sameObject(feature.experiment.variations, [false, true]) || (result.value !== undefined && result.value !== feature.value)) throw Object.assign(Error('Delivery visitor not enrolled'), {retired: true});
      const next = {v: 1, experiment: EXPERIMENT, revision: REVISION, id, phase: String(rule.phase), arm: variation === 1 ? 'B' : 'A', expiresAt: stored?.expiresAt || Date.now() + TTL, acknowledged: stored?.phase === String(rule.phase) && stored.arm === (variation === 1 ? 'B' : 'A') && stored.acknowledged === true};
      if (stored?.phase === next.phase && stored.arm !== next.arm) throw Error('Delivery assignment changed within phase');
      if (!stored || !sameObject(stored, next)) {
        if (!writeCookie(ASSIGNMENT, JSON.stringify(next), Math.max(1, Math.floor((next.expiresAt - Date.now()) / 1000)))) throw Error('Delivery assignment storage unavailable');
      }
      selected = feature; assignment = next; lastError = '';
      window.IslanderExperimentAttribution?.configure(features);
      applyPolicy(policy(next.arm, next.phase));
    }).catch(error => {
      selected = undefined; lastError = 'experiment_unavailable';
      const saved = !error.retired && displayMode() !== 'off' && !optedOut() ? readAssignment() : undefined;
      if (saved?.acknowledged === true) {
        // A network/SDK failure does not withdraw an offer that Shopify already acknowledged.
        assignment = saved;
        acknowledgedPolicy = JSON.stringify(policy(saved.arm, saved.phase));
        applyPolicy(policy(saved.arm, saved.phase));
      } else {
        assignment = undefined; removeMeasurement();
        if (error.retired) writeCookie(ASSIGNMENT, '', 0);
        applyPolicy(policy());
      }
    }).finally(() => { window.clearTimeout(timer); featureRequest = null; });
    return featureRequest;
  };
  const setEnabled = enabled => {
    if (qa) return;
    emergencyOptout = false;
    const saved = writeCookie(OPTOUT, enabled ? '0' : '1', 365 * 86400);
    if (!saved && !enabled) emergencyOptout = true;
    lastOptout = optedOut();
    if (!enabled) writeCookie(ASSIGNMENT, '', 0);
    refresh();
  };
  const inspect = () => ({
    mode: qa ? 'qa' : 'production', country: config.country, currency: cart.currency,
    display: displayMode(), optedOut: optedOut(), assigned: assignment?.arm || null, phase: assignment?.phase || null,
    rendered: view().current.arm, threshold: view().threshold, subtotal: subtotal(),
    policy: JSON.parse(desired), cart: qa ? 'not-written' : desired === synced ? 'synced' : running ? 'pending' : 'failed', error: lastError,
  });
  const ready = Promise.resolve().then(refresh);
  window.IslanderDelivery = Object.freeze({ready, inspect, refresh, refreshCart, flush, syncForms, setEnabled});
  if (!window.customElements.get('islander-delivery-guidance')) {
    window.customElements.define('islander-delivery-guidance', class extends HTMLElement {
      connectedCallback() { renderGuidance(this); }
    });
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      cartGeneration++;
      if (!useCart(event?.cartData)) { cartFresh = false; render(); refreshCart(); }
      syncForms();
    });
    subscribe(PUB_SUB_EVENTS.variantChange, syncForms);
  }
  document.addEventListener('visitorConsentCollected', productionExposure);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', productionExposure);
  document.addEventListener('islander:experiment-attribution-ready', productionExposure);
  document.addEventListener('islander:announcement-change', productionExposure, true);
  document.addEventListener('shopify:section:load', () => { syncForms(); render(); });
  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-delivery-test-enabled]')) setEnabled(event.target.checked);
  });
  document.addEventListener('product-info:loaded', () => window.queueMicrotask(syncForms));
  for (const eventName of ['pointerdown', 'focusin']) document.addEventListener(eventName, event => {
    if (event.target.closest?.('form[action*="/cart/add"], .shopify-payment-button, shopify-accelerated-checkout')) syncForms();
    if (event.target.closest?.('.shopify-payment-button, shopify-accelerated-checkout, .additional-checkout-buttons, [data-shopify-buttoncontainer]')) {
      reconcileShared(); syncForms(); flush();
    }
  }, true);
  const wallet = target => target?.closest?.('.shopify-payment-button, shopify-accelerated-checkout, .additional-checkout-buttons, [data-shopify-buttoncontainer]');
  document.addEventListener('submit', event => {
    syncForms();
    const form = event.target, submitter = event.submitter, destination = localFormURL(form);
    if (qa || window.IslanderExperimentAttribution || event.defaultPrevented || wallet(submitter) || !destination ||
      !(/\/checkout\/?$/.test(destination.pathname) || (/\/cart\/?$/.test(destination.pathname) && (submitter?.name === 'checkout' || (!submitter && form.querySelector('[name="checkout"]')))))) return;
    if (checkoutReady.get(form) === desired) { checkoutReady.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    if (pendingCheckout.has(form)) return;
    pendingCheckout.add(form);
    flush().finally(() => {
      pendingCheckout.delete(form); checkoutReady.set(form, desired);
      try { form.requestSubmit(submitter?.form === form ? submitter : undefined); }
      catch {
        if (/\/cart\/?$/.test(destination.pathname) && !form.querySelector('input[type="hidden"][name="checkout"]')) {
          const input = document.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; input.value = submitter?.value || 'Checkout'; form.append(input);
        }
        window.HTMLFormElement.prototype.submit.call(form);
      }
    });
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (qa || window.IslanderExperimentAttribution || !link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
      wallet(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    let destination;
    try { destination = new URL(link.href); } catch { return; }
    if (destination.origin !== url.origin || !/\/checkout\/?$/.test(destination.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (pendingCheckout.has(link)) return;
    pendingCheckout.add(link);
    flush().finally(() => window.location.assign(destination.href));
  }, true);
  const resume = () => {
    if (document.visibilityState === 'hidden') return;
    reconcileShared();
    refreshCart();
    flush();
    const changed = lastOptout !== optedOut();
    lastOptout = optedOut();
    if (changed || optedOut() || !active()) refresh();
    else if (Date.now() - lastFeatureCheck > 60000) refresh();
    else { syncForms(); render(); }
  };
  window.addEventListener('focus', resume);
  document.addEventListener('visibilitychange', resume);
  if (!qa && typeof window.BroadcastChannel === 'function') {
    try {
      channel = new window.BroadcastChannel('islander_delivery_v1');
      channel.addEventListener('message', () => { reconcileShared(); syncForms(); render(); });
    } catch {}
  }
  window.addEventListener('pageshow', event => { if (event.persisted) { refreshCart(); refresh(); } });
  syncForms(); render(); pump();
})();
